import React, { useEffect, useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import axios from 'axios';

import { useDonationState } from './useDonationState';
import { useAmountValidation } from './useAmountValidation';
import { useEmailValidation } from './useEmailValidation';
import { getStepTitle } from './donationConstants';
import { PREDEFINED_AMOUNTS } from './donationConfig';
import { EXTERNAL_PAYMENT_URLS, handleExternalPaymentClick } from './externalPayments';
import { CRYPTO_WALLETS, generateQRCode, copyCryptoAddress } from './cryptoWallets';

import StepIndicator from './StepIndicator';
import StripeCheckout from './StripeCheckout';
import ApplePayButton from './ApplePayButton';
import ExternalPaymentButton, { GoFundMeIcon, PayPalIcon } from './ExternalPaymentButton';
import CryptoWallet from './CryptoWallet';

const MF_API = process.env.REACT_APP_MAGIC_FOREST_API_URL || 'https://magicforest.land';
const STRIPE_PK = process.env.REACT_APP_MAGIC_FOREST_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

/**
 * DonationContent — headless donation flow content.
 * Renders inside EditorPanel's scrollable area (below the LCD header).
 * ONE single field-container per step — matching Magic Forest UX layout.
 */
export default function DonationContent({ onBack, userEmail }) {
  if (!STRIPE_PK) {
    return (
      <div className="field-container">
        <div className="text-center py-4">
          <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-3">
            <svg className="w-5 h-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </div>
          <p className="text-white/70 text-sm mb-1">Donation not available</p>
          <p className="text-white/40 text-xs mb-4">Missing Stripe configuration.</p>
          <button onClick={onBack} className="text-white/40 hover:text-white text-sm transition-colors">Cancel</button>
        </div>
      </div>
    );
  }
  return <DonationContentInner onBack={onBack} userEmail={userEmail} />;
}

function DonationContentInner({ onBack, userEmail }) {
  const state = useDonationState(onBack, userEmail);
  const emailValidation = useEmailValidation();
  const amountValidation = useAmountValidation();

  const {
    donationStep, donationType, donationAmount, customAmount, isCustomAmountManuallyEdited,
    donorEmail, donorName, gdprConsent, isSuccess,
    paymentIntentId, selectedPaymentMethod,
    receiptEmail, isReceiptSending, isReceiptSent, subscriptionInfo,
    setDonationType, setDonationAmount, setCustomAmount, setIsCustomAmountManuallyEdited,
    setDonorEmail, setDonorName, setGdprConsent, setIsSubmitting, setIsSuccess,
    setPaymentIntentId, setSelectedPaymentMethod,
    setReceiptEmail, setIsReceiptSending, setIsReceiptSent, setSubscriptionInfo,
    goToStep, closeDonationPanel,
  } = state;

  // Crypto state
  const [copiedAddress, setCopiedAddress] = useState(null);
  const cryptoQRCodes = useMemo(() => {
    const map = new Map();
    CRYPTO_WALLETS.forEach(w => map.set(w.address, generateQRCode(w.address)));
    return map;
  }, []);

  useEffect(() => {
    if (donationStep === 2 && !customAmount && !isCustomAmountManuallyEdited) {
      setDonationAmount(10);
      setCustomAmount('10');
    }
  }, [donationStep, customAmount, isCustomAmountManuallyEdited, setDonationAmount, setCustomAmount]);

  // ── Handlers ──
  const handlePaymentMethodSelect = (method) =>
    setSelectedPaymentMethod(selectedPaymentMethod === method ? null : method);

  const handleAmountSelect = (amount) =>
    amountValidation.handleAmountSelect(amount, donationType, { setDonationAmount, setCustomAmount, setIsCustomAmountManuallyEdited });

  const handleCustomAmountChange = (e) =>
    amountValidation.handleCustomAmountChange(e, donationType, { setCustomAmount, setDonationAmount, setIsCustomAmountManuallyEdited });

  const handleStripePaymentSuccess = (paymentIntent) => {
    const amt = customAmount ? parseInt(customAmount, 10) : donationAmount;
    const isMonthly = paymentIntent.type === 'subscription' || paymentIntent.subscriptionId;
    const bd = paymentIntent.billingDetails || {};
    setPaymentIntentId(paymentIntent.id || paymentIntent.subscriptionId);
    if (isMonthly) {
      setSubscriptionInfo({ subscriptionId: paymentIntent.subscriptionId, email: bd.email || donorEmail, name: bd.name || donorName, amount: amt, isMonthly: true });
    }
    setIsSubmitting(false);
    setIsSuccess(true);
  };

  const handleStripePaymentError = (error) => {
    console.error('Payment error:', error);
    setIsSubmitting(false);
    if (error.message?.includes('email address')) emailValidation.showEmailValidationError(donationType, donorEmail);
  };

  const handleSendReceipt = async () => {
    if (!receiptEmail.trim() || !paymentIntentId) return;
    setIsReceiptSending(true);
    try {
      await axios.post(`${MF_API}/api/update-receipt-email`, { payment_intent_id: paymentIntentId, receipt_email: receiptEmail.trim() });
      setIsReceiptSending(false);
      setIsReceiptSent(true);
    } catch { setIsReceiptSending(false); }
  };

  const isCardPaymentEnabled = () => emailValidation.isCardPaymentEnabled(donationType, donorEmail);
  const showEmailValidationError = () => emailValidation.showEmailValidationError(donationType, donorEmail);
  const handleEmailChange = (e) => emailValidation.handleEmailChange(e, setDonorEmail);
  const handleBillingDetailsCollected = (bd) => { if (bd.name) setDonorName(bd.name); };

  const handleCopyToClipboard = async (address, currency) => {
    const ok = await copyCryptoAddress(address, currency);
    if (ok) { setCopiedAddress(address); setTimeout(() => setCopiedAddress(null), 3000); }
  };

  const currentAmount = customAmount ? parseInt(customAmount, 10) : donationAmount;

  // ── Success State ──
  if (isSuccess) {
    return (
      <div className="field-container">
        <div className="text-center py-2">
          <div className="w-12 h-12 bg-emerald-500 rounded-full mx-auto flex items-center justify-center mb-4">
            <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          {subscriptionInfo?.isMonthly ? (
            <>
              <p className="mb-3 text-base text-white">€{subscriptionInfo.amount}/month active!</p>
              <p className="mb-4 text-white/60 text-sm">
                Manage or cancel anytime via email to:<br />
                <span className="text-white font-medium">{subscriptionInfo.email}</span>
              </p>
              <button onClick={closeDonationPanel} className="btn-instrument btn-instrument-green px-5 py-1.5 text-sm">Done</button>
            </>
          ) : (
            <>
              <p className="mb-1 text-base font-medium text-white">Thank You!</p>
              <p className="mb-5 text-sm text-white/60">Your €{customAmount || donationAmount} donation helps Magic Forest grow.</p>
              <div className="text-left">
                <label className="block text-xs font-medium text-white/50 mb-1.5">Want a receipt?</label>
                <input type="email" value={receiptEmail} onChange={(e) => setReceiptEmail(e.target.value)}
                  className="block w-full px-3 py-2 rounded-lg bg-neutral-800/80 border border-neutral-700 text-white text-sm focus:ring-1 focus:ring-emerald-500/40 focus:border-emerald-500/40 outline-none"
                  placeholder="your.email@example.com" />
                {isReceiptSent && <p className="mt-1.5 text-xs text-emerald-400">✓ Receipt sent to {receiptEmail}</p>}
              </div>
              <div className="flex gap-2 justify-center mt-4">
                <button onClick={closeDonationPanel} className="text-white/40 hover:text-white text-sm px-4 py-1.5 transition-colors">Close</button>
                <button onClick={handleSendReceipt} disabled={!receiptEmail.trim() || isReceiptSending || isReceiptSent}
                  className="btn-instrument btn-instrument-green px-4 py-1.5 text-sm">
                  {isReceiptSending ? 'Sending...' : isReceiptSent ? 'Sent!' : 'Send Receipt'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════
  // Step 1: Choose Donation Type
  // ══════════════════════════════════════════════════════════════
  if (donationStep === 1) {
    return (
      <div className="field-container">
        {/* Title */}
        <div className="mb-4">
          <h3 className="text-base font-semibold text-white/90">Choose Donation Type</h3>
        </div>

        {/* Step indicator */}
        <StepIndicator currentStep={1} isSuccess={false} />

        {/* Type toggle */}
        <div className="bg-neutral-800/60 p-1 rounded-lg flex mb-5">
          <button
            className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${donationType === 'one-time' ? 'bg-neutral-700 text-white shadow-sm' : 'text-white/50 hover:text-white/70'}`}
            onClick={() => { setDonationType('one-time'); amountValidation.setSubscriptionWarning(''); }}
          >One-time</button>
          <button
            className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${donationType === 'monthly' ? 'bg-neutral-700 text-white shadow-sm' : 'text-white/50 hover:text-white/70'}`}
            onClick={() => setDonationType('monthly')}
          >Monthly</button>
        </div>

        {/* Description */}
        <p className="text-center text-white/50 text-sm mb-6">
          {donationType === 'one-time'
            ? 'Make a one-time contribution to our forest initiative'
            : 'Set up a recurring monthly donation for ongoing support'}
        </p>

        {/* Continue */}
        <div className="flex justify-end">
          <button onClick={() => goToStep(2)} className="btn-instrument btn-instrument-green px-5 py-1.5 text-sm">Continue</button>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════
  // Step 2: Select Amount
  // ══════════════════════════════════════════════════════════════
  if (donationStep === 2) {
    return (
      <div className="field-container">
        {/* Title */}
        <div className="mb-4">
          <h3 className="text-base font-semibold text-white/90">Select Amount</h3>
        </div>

        {/* Step indicator */}
        <StepIndicator currentStep={2} isSuccess={false} />

        {/* Predefined amounts */}
        <div className="grid grid-cols-2 gap-2 mb-5">
          {PREDEFINED_AMOUNTS.map((amt) => (
            <button key={amt} onClick={() => handleAmountSelect(amt)}
              className={`py-2.5 rounded-lg border text-sm font-medium transition-all ${donationAmount === amt ? 'border-emerald-500 bg-emerald-500/10 text-white' : 'border-neutral-700 text-white/70 hover:border-emerald-400/30 hover:text-white'}`}>
              €{amt}
            </button>
          ))}
        </div>

        {/* Custom amount */}
        <div className="mb-6">
          <label className="block text-xs font-medium text-white/50 mb-1">Custom amount</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><span className="text-white/50">€</span></div>
            <input type="text" inputMode="numeric" value={customAmount} onChange={handleCustomAmountChange}
              onFocus={(e) => { setTimeout(() => { const l = e.target.value.length; e.target.setSelectionRange(l, l); }, 0); }}
              className="block w-full pl-8 pr-3 py-2 rounded-lg bg-neutral-800/80 border border-neutral-700 text-white text-sm focus:ring-1 focus:ring-emerald-500/40 focus:border-emerald-500/40 outline-none"
              placeholder="Enter amount (€1–€99)" />
          </div>
          {amountValidation.amountError && <p className="text-red-400 text-xs mt-1">{amountValidation.amountError}</p>}
          {amountValidation.subscriptionWarning && <p className="text-amber-400 text-xs mt-1">{amountValidation.subscriptionWarning}</p>}
        </div>

        {/* Back + Continue */}
        <div className="flex items-center justify-between">
          <button onClick={() => goToStep(1)} className="text-white/30 hover:text-white/70 text-xs transition-colors">← Back</button>
          <button onClick={() => goToStep(3)} disabled={!amountValidation.isValidForProceed(customAmount)}
            className="btn-instrument btn-instrument-green px-5 py-1.5 text-sm">Continue</button>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════
  // Step 3: Payment
  // ══════════════════════════════════════════════════════════════
  return (
    <div className="field-container">
      {/* Title + Back */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-white/90">Payment</h3>
        <button onClick={() => goToStep(2)} className="text-white/30 hover:text-white/70 text-xs transition-colors">← Back</button>
      </div>

      {/* Step indicator */}
      <StepIndicator currentStep={3} isSuccess={false} />

      {/* Summary */}
      <div className="p-3 bg-neutral-800/40 rounded-lg mb-4">
        <div className="flex justify-between mb-1.5">
          <span className="text-white/50 text-sm">Amount:</span>
          <span className="text-sm font-medium text-white">€{customAmount}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-white/50 text-sm">Type:</span>
          <span className="text-sm font-medium text-white capitalize">{donationType === 'one-time' ? 'One-Time' : 'Monthly'}</span>
        </div>
      </div>

      {/* Email for monthly */}
      {donationType === 'monthly' && (
        <div className="mb-4">
          <label htmlFor="donor-email" className="block text-xs font-medium text-white/60 mb-1.5">
            Email address (required for subscriptions)
          </label>
          <input id="donor-email" type="email" value={donorEmail} onChange={handleEmailChange}
            className={`w-full p-2.5 bg-neutral-800/80 rounded-lg border text-sm text-white placeholder-white/30 focus:outline-none focus:ring-1 transition-colors ${emailValidation.emailError ? 'border-red-500 focus:ring-red-500/40' : 'border-neutral-700 focus:ring-emerald-500/40 focus:border-emerald-500/40'}`}
            placeholder="your.email@example.com" required />
          {emailValidation.emailError && <p className="text-red-400 text-xs mt-1">{emailValidation.emailError}</p>}
          <p className="text-white/40 text-xs mt-1.5">Required to manage your subscription.</p>
        </div>
      )}

      {/* Monthly payment disclosure */}
      {donationType === 'monthly' && (
        <div className="mb-4 p-2.5 bg-neutral-800/20 rounded-lg border border-neutral-700/50">
          <p className="text-xs text-white/50 leading-relaxed">
            <strong>Payment Processing:</strong> Monthly recurring donation of €{customAmount}.
            First payment today, then monthly on the same date.
            <strong> Cancel anytime</strong> via your email receipt link.
          </p>
        </div>
      )}

      {/* Payment methods */}
      <div className="text-xs font-medium text-white/50 mb-2.5">Payment method</div>
      <div className="space-y-2 mb-5">

        {/* Card */}
        <div
          className={`bg-neutral-800/60 p-2.5 rounded-lg flex items-center text-sm cursor-pointer transition-colors ${selectedPaymentMethod === 'card' ? 'ring-1 ring-emerald-500 text-white' : 'text-white/70'} ${!isCardPaymentEnabled() ? 'opacity-40 cursor-not-allowed' : 'hover:text-white'}`}
          onClick={() => {
            if (donationType === 'monthly' && !isCardPaymentEnabled()) { showEmailValidationError(); return; }
            handlePaymentMethodSelect('card');
          }}
        >
          <svg className="h-4 w-4 mr-2.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
          Credit / Debit Card
        </div>
        {selectedPaymentMethod === 'card' && (
          <div className="p-3 rounded-lg bg-neutral-900/60 border border-neutral-700/50">
            <Elements stripe={stripePromise}>
              <StripeCheckout
                amount={currentAmount}
                currency="eur"
                donationType={donationType}
                referrer={undefined}
                gdprConsent={gdprConsent}
                onGdprConsentChange={setGdprConsent}
                onSuccess={handleStripePaymentSuccess}
                onError={handleStripePaymentError}
                donorEmail={donorEmail}
              />
            </Elements>
            <div className="flex items-center justify-center pt-2 pb-0.5">
              <div className="flex items-center gap-1.5 text-white/35 text-xs">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
                Secured & powered by Stripe
              </div>
            </div>
          </div>
        )}

        {/* Apple / Google Pay */}
        <Elements stripe={stripePromise}>
          <ApplePayButton
            amount={currentAmount}
            currency="eur"
            donationType={donationType}
            referrer={undefined}
            gdprConsent={gdprConsent}
            onSuccess={handleStripePaymentSuccess}
            onError={handleStripePaymentError}
            donorEmail={donorEmail}
            onBillingDetailsCollected={handleBillingDetailsCollected}
            isSelected={selectedPaymentMethod === 'wallet'}
            onSelect={() => handlePaymentMethodSelect('wallet')}
          />
        </Elements>

        {/* GoFundMe + PayPal (one-time only) */}
        {donationType !== 'monthly' && (
          <>
            <ExternalPaymentButton name="GoFundMe" onClick={() => handleExternalPaymentClick(EXTERNAL_PAYMENT_URLS.GOFUNDME)} icon={<GoFundMeIcon />} />
            <ExternalPaymentButton name="PayPal" onClick={() => handleExternalPaymentClick(EXTERNAL_PAYMENT_URLS.PAYPAL)} icon={<PayPalIcon />} />
          </>
        )}

        {/* Crypto (one-time only) */}
        {donationType !== 'monthly' && (
          <>
            <div
              className={`bg-neutral-800/50 p-2.5 rounded-lg flex items-center text-sm cursor-pointer transition-colors ${selectedPaymentMethod === 'crypto' ? 'ring-1 ring-emerald-500 text-white' : 'text-white/70 hover:text-white'}`}
              onClick={() => handlePaymentMethodSelect('crypto')}
            >
              <svg className="h-4 w-4 mr-2.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Crypto
            </div>
            {selectedPaymentMethod === 'crypto' && (
              <div className="p-3 bg-neutral-900/50 rounded-lg border border-neutral-700/50">
                <p className="text-white/60 text-xs mb-3 text-center">Donate in crypto — safe and direct.</p>
                <div className="space-y-3">
                  {CRYPTO_WALLETS.map((wallet, i) => (
                    <CryptoWallet key={i} wallet={wallet} qrCodeUrl={cryptoQRCodes.get(wallet.address)} copiedAddress={copiedAddress} onCopyAddress={handleCopyToClipboard} />
                  ))}
                </div>
                <div className="mt-3 p-2.5 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                  <p className="text-amber-200/80 text-xs text-center">
                    Please double-check the wallet address before sending. Crypto transactions cannot be reversed.
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
